import { useParams } from "react-router-dom";
import { useSite, usePublic } from "../../lib/site";
import { stripHtml, href } from "../../lib/i18n";
import { lazyImgs } from "../../lib/html";
import { Seo, breadcrumbLd } from "../../components/site/Seo";
import { Img, Socials } from "../../components/site/Common";
import { TrainingsTimeline } from "../../components/site/TrainingsTimeline";
import { PressGrid } from "../../components/site/PressGrid";
import Works from "./Works";
import NotFound, { Loading } from "./NotFound";
import { inScope } from "./PublicLayout";

export function PageHeader({ eyebrow, title, sub }) {
  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-10 rise">
      {eyebrow && <p className="eyebrow mb-5">{eyebrow}</p>}
      <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="page-title">{title}</h1>
      {sub && <p className="mt-6 text-lg text-s-muted">{sub}</p>}
    </div>
  );
}

export default function PageView() {
  const { slug } = useParams();
  const { lang, t, ui, site } = useSite();
  const { data: p, isLoading, isError } = usePublic(["page", slug], `/public/pages/${slug}`);
  if (isLoading) return <Loading />;
  if (isError || !p || !inScope(p.theme_scope, site.theme?.mode === "adaylik" ? "adaylik" : "normal")) return <NotFound />;
  const title = t(p.seo?.title) || t(p.title);
  const desc = t(p.seo?.description) || stripHtml(t(p.body)).slice(0, 155);
  const seo = <Seo title={title} description={desc} image={p.seo?.image || p.cover}
    jsonLd={breadcrumbLd([{ name: t(site.general?.site_name), path: href(lang, "home") }, { name: t(p.title), path: href(lang, "page", p.slug) }])} />;
  if (p.slug === "ben-kimim") {
    const photo = p.cover || site.profile?.blog_photo || site.profile?.photo;
    const hover = p.benkimim_photo_hover !== false;
    const FADE = "radial-gradient(ellipse 45% 70% at 50% 50%, #000 0%, #000 55%, rgba(0,0,0,0.35) 80%, transparent 100%)";
    const roleText = t(site.profile?.tagline) || "";
    const nameText = site.profile?.name || "";
    return (
      <article data-testid="page-view" className="pb-28 sm:pb-36 overflow-x-clip">
        {seo}
        <div className={`max-w-6xl mx-auto px-5 sm:px-8 pt-20 sm:pt-28 grid gap-12 lg:gap-20 items-start ${photo ? "lg:grid-cols-[0.75fr_1.25fr]" : ""}`}>
          {photo && (
            <div className="relative rise lg:sticky lg:top-28 group overflow-hidden" data-testid="benkimim-photo-wrap">
              <div className={`absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 w-[115%] h-[70%] rounded-full bg-[#d4af3722] blur-[100px] transition-colors duration-500 ${hover ? "group-hover:bg-[#d4af3740]" : ""}`} aria-hidden />
              {/* Blurred backdrop copy — turns the photo's hard dark rectangle
                  into an ambient halo that fades into the page background. */}
              <Img src={photo} alt="" aria-hidden
                className="absolute inset-0 w-full h-full object-cover scale-110 blur-[60px] opacity-70"
                style={{ WebkitMaskImage: FADE, maskImage: FADE }} />
              <Img src={photo} alt={t(p.title)} loading="eager" data-testid="benkimim-photo"
                className={`relative w-full aspect-[4/5] object-cover object-top transition-transform duration-500 ${hover ? "group-hover:scale-[1.03] group-hover:-translate-y-1.5" : ""}`}
                style={{ WebkitMaskImage: FADE, maskImage: FADE }} />
            </div>
          )}
          <div className="rise">
            <p className="eyebrow mb-6" data-testid="benkimim-eyebrow">{lang === "tr" ? "Hakkımda" : "About"}</p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95]" data-testid="page-title">{t(p.title)}</h1>
            <div className="mt-7 flex items-center gap-4" aria-hidden>
              <span className="h-px w-10 bg-s-primary" />
              {roleText && <span className="eyebrow text-s-muted" style={{ color: "var(--s-muted)" }}>{roleText}</span>}
            </div>
            {stripHtml(t(p.body)) && <div className="prose-site prose-lg mt-10 benkimim-lead" data-testid="page-body" dangerouslySetInnerHTML={{ __html: lazyImgs(t(p.body)) }} />}
            {site.profile?.socials?.length > 0 && (
              <div className="mt-12 pt-8 border-t border-site-border">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="eyebrow mb-1">{ui.follow}</p>
                    {nameText && <p className="font-heading text-base" style={{ color: "var(--s-text)" }}>— {nameText}</p>}
                  </div>
                  <Socials socials={site.profile.socials} />
                </div>
              </div>
            )}
          </div>
        </div>
      </article>
    );
  }
  if (p.slug === "vizyonum") {
    const nameText = site.profile?.name || "";
    return (
      <article data-testid="page-view" className="pb-28 sm:pb-36">
        {seo}
        <div className="relative overflow-hidden">
          <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[520px] h-[260px] rounded-full bg-[#d4af370f] blur-[140px]" aria-hidden />
          <div className="relative max-w-3xl mx-auto px-5 sm:px-8 pt-24 sm:pt-32 rise">
            <div className="flex items-center gap-4 mb-8" aria-hidden>
              <span className="font-heading text-s-primary text-sm tracking-[0.3em]" style={{ color: "var(--s-primary)" }}>01</span>
              <span className="h-px flex-1 bg-site-border" />
              <span className="eyebrow">{lang === "tr" ? "Vizyon" : "Vision"}</span>
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95]" data-testid="page-title">{t(p.title)}</h1>
            <p className="mt-6 text-base sm:text-lg max-w-xl leading-relaxed" style={{ color: "var(--s-muted)" }}>
              {lang === "tr" ? "Gelecek için düşüncelerim, önceliklerim ve yolum." : "My thoughts, priorities and path for the future."}
            </p>
          </div>
        </div>
        {p.cover && (
          <div className="max-w-5xl mx-auto px-5 sm:px-8 mt-16">
            <Img src={p.cover} alt={t(p.title)} loading="eager" className="w-full aspect-[16/7] object-cover rounded-xl border border-site-border" />
          </div>
        )}
        <div className="max-w-2xl mx-auto px-5 sm:px-8 mt-16 sm:mt-20 relative vizyonum-prose">
          <span className="absolute left-0 top-2 bottom-2 w-px bg-gradient-to-b from-s-primary via-s-primary/40 to-transparent" aria-hidden />
          <div className="prose-site prose-lg pl-8" data-testid="page-body" dangerouslySetInnerHTML={{ __html: lazyImgs(t(p.body)) }} />
          <div className="mt-16 pt-8 border-t border-site-border flex items-center gap-4">
            <span className="h-px w-10 bg-s-primary" aria-hidden />
            <span className="eyebrow" style={{ color: "var(--s-muted)" }}>{nameText || (lang === "tr" ? "İmza" : "Signature")}</span>
          </div>
        </div>
      </article>
    );
  }
  if (p.slug === "calismalarim") {
    return <>{seo}<Works page={p} lang={lang} t={t} /></>;
  }
  if (p.slug === "egitimlerim") {
    return (
      <article data-testid="page-view" className="pb-24">
        {seo}
        <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-16 sm:pt-20 rise">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight" data-testid="page-title">{t(p.title)}</h1>
          {stripHtml(t(p.body)) && <p className="mt-3 text-s-muted leading-relaxed">{stripHtml(t(p.body))}</p>}
        </div>
        <TrainingsTimeline page={p} />
      </article>
    );
  }
  return (
    <article data-testid="page-view">
      {seo}
      <PageHeader title={t(p.title)} />
      {p.cover && (
        <div className="max-w-6xl mx-auto px-5 sm:px-8 mb-14">
          <Img src={p.cover} alt={t(p.title)} className="w-full aspect-[21/9] object-cover rounded-3xl" />
        </div>
      )}
      {stripHtml(t(p.body)) && <div className="max-w-3xl mx-auto px-5 sm:px-8 prose-site" data-testid="page-body" dangerouslySetInnerHTML={{ __html: lazyImgs(t(p.body)) }} />}
      {p.slug === "basin-arsivi" && <PressGrid />}
    </article>
  );
}
